import { ReactComponent as CloseIcon } from '@/assets/icons/close.svg';
import { ExclamationIcon } from '@/components/common/icon';
import WebModal from '@/components/web/components/Modal';
import type { CreatorPlatformCollaborationRequirement } from '@/interface/creatorOrderV2';
import { scrollElementToTop } from '@/utils/scrollElementToTop';
import { Alert, Button } from 'aha-components';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function CreatorCollaborationRequirementNotice({
  requirement,
  validationAttempt = 0
}: {
  requirement: CreatorPlatformCollaborationRequirement;
  validationAttempt?: number;
}) {
  const { t } = useTranslation();
  const noticeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const element = noticeRef.current;
    if (!element || !validationAttempt || requirement !== 'required') return;
    scrollElementToTop(element);
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    element.getAnimations?.().forEach((animation) => animation.cancel());
    const animation = element.animate?.(
      [0, -6, 6, -4, 4, 0].map((x) => ({ transform: `translateX(${x}px)` })),
      { duration: 480, easing: 'ease-in-out' }
    );
    return () => animation?.cancel();
  }, [requirement, validationAttempt]);

  if (requirement === 'single') return null;
  const required = requirement === 'required';
  const title = required
    ? t('multi_platform.quote.collaboration_requirement.required_title', {
        defaultValue: 'Multi-channel application required'
      })
    : t('multi_platform.quote.collaboration_requirement.preferred_title', {
        defaultValue: 'The brand prefers multi-channel collaborations'
      });
  const description = required
    ? t('multi_platform.quote.collaboration_requirement.required_description', {
        defaultValue:
          'This collaboration requires at least two different channels or accounts. Please add another account to submit your application.'
      })
    : t(
        'multi_platform.quote.collaboration_requirement.preferred_description',
        {
          defaultValue:
            'Add another channel or account to improve your chances of being selected and potentially earn more. You can still apply with a single channel.'
        }
      );

  return (
    <div
      ref={noticeRef}
      role={required && validationAttempt > 0 ? 'alert' : 'note'}
      aria-label={title}
      data-collaboration-requirement={requirement}
    >
      <Alert
        key={`${title}:${description}`}
        type={required ? 'warning' : 'info'}
        title={title}
        description={description}
        className="!rounded-lg"
      />
    </div>
  );
}

export function CreatorSingleChannelConfirmation({
  visible,
  mobile,
  canAddAccounts,
  onClose,
  onSubmit,
  onAddAccounts
}: {
  visible: boolean;
  mobile: boolean;
  canAddAccounts: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onAddAccounts: () => void;
}) {
  const { t } = useTranslation();
  const title = t(
    'multi_platform.quote.collaboration_requirement.confirm_title',
    {
      defaultValue: 'Apply with a single channel?'
    }
  );
  const description = t(
    'multi_platform.quote.collaboration_requirement.confirm_description',
    {
      defaultValue:
        'The brand prefers multi-channel collaborations. Adding another channel or account may improve your chances of being selected and help you earn more.'
    }
  );

  return (
    <WebModal
      visible={visible}
      width={420}
      className="!max-w-[calc(100vw-32px)] !items-stretch !rounded-xl !p-4"
      modalStyle={{ zIndex: mobile ? 10001 : 1500 }}
      isShowHeader={false}
      isShowFooter={false}
      canClose
      manageFocus
      ariaLabel={title}
      handleClose={onClose}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="flex h-8 w-8 items-center justify-center rounded-full border border-[#FDE5BD] text-[#F79009]">
          <ExclamationIcon className="h-5 w-5" />
        </span>
        <button
          type="button"
          className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 text-[#A3A3A3]"
          aria-label={t('common.close', { defaultValue: 'Close' })}
          onClick={onClose}
        >
          <CloseIcon className="h-5 w-5" />
        </button>
      </div>
      <p
        key={title}
        className="m-0 text-sm font-semibold leading-5 text-[#404040]"
      >
        {title}
      </p>
      <p
        key={description}
        className="mb-4 mt-1 text-sm leading-5 text-[#525252]"
      >
        {description}
      </p>
      <div className="flex flex-wrap items-center gap-4">
        <Button
          type="link-gray"
          size="sm"
          className="!h-auto !p-0"
          onClick={onSubmit}
        >
          {t('multi_platform.quote.collaboration_requirement.submit_anyway', {
            defaultValue: 'Submit anyway'
          })}
        </Button>
        {canAddAccounts ? (
          <Button
            type="link-color"
            size="sm"
            className="!h-auto !p-0"
            onClick={onAddAccounts}
          >
            {t('multi_platform.quote.add_accounts', {
              defaultValue: 'Add accounts'
            })}
          </Button>
        ) : null}
      </div>
    </WebModal>
  );
}
